import { supabase } from '@/lib/supabase';
import { oneSignalService } from './oneSignalService';
import { Platform, Linking } from 'react-native';

export interface NotificationPreferences {
  new_episodes: boolean;
  recommendations: boolean;
  subscription_reminders: boolean;
  rewards: boolean;
  marketing: boolean;
}

export interface NotificationPreferencesWithStatus extends NotificationPreferences {
  push_enabled: boolean;
  can_request_permission: boolean;
}

class NotificationPreferencesService {
  async getPreferences(userId?: string): Promise<NotificationPreferencesWithStatus | null> {
    try {
      let data = null;

      if (userId) {
        // Get database preferences for authenticated user
        const result = await supabase
          .from('user_notification_preferences')
          .select('new_episodes, recommendations, subscription_reminders, rewards, marketing')
          .eq('smartuser_id', userId)
          .maybeSingle();

        if (result.error && result.error.code !== 'PGRST116') {
          const errorDetails = {
            code: result.error.code,
            message: result.error.message,
            details: result.error.details,
            hint: result.error.hint
          };
          console.error('[NotificationPreferences] Error fetching preferences:', JSON.stringify(errorDetails));
          return null;
        }
        data = result.data;
      } else {
        // Get anonymous preferences (smartuser_id IS NULL)
        const result = await supabase
          .from('user_notification_preferences')
          .select('new_episodes, recommendations, subscription_reminders, rewards, marketing')
          .is('smartuser_id', null)
          .maybeSingle();

        if (result.error && result.error.code !== 'PGRST116') {
          const errorDetails = {
            code: result.error.code,
            message: result.error.message,
            details: result.error.details,
            hint: result.error.hint
          };
          console.error('[NotificationPreferences] Error fetching anonymous preferences:', JSON.stringify(errorDetails));
        }
        data = result.data;
      }

      // Get system permission status (skip on web)
      let pushEnabled = false;
      let canRequestPermission = false;

      if (Platform.OS !== 'web') {
        try {
          pushEnabled = await oneSignalService.hasPermission();
          canRequestPermission = !pushEnabled;
        } catch (error) {
          console.error('[NotificationPreferences] Error checking permission:', error);
        }
      }

      // Return preferences with defaults if not found
      const preferences = data || {
        new_episodes: true,
        recommendations: true,
        subscription_reminders: true,
        rewards: true,
        marketing: false,
      };

      return {
        ...preferences,
        push_enabled: pushEnabled,
        can_request_permission: canRequestPermission,
      };
    } catch (error) {
      console.error('[NotificationPreferences] Error getting preferences:', error);
      return null;
    }
  }

  async updatePreferences(
    userId: string | undefined,
    preferences: Partial<NotificationPreferences>
  ): Promise<boolean> {
    try {
      if (userId) {
        // Handle authenticated user preferences
        const { data: existing } = await supabase
          .from('user_notification_preferences')
          .select('id')
          .eq('smartuser_id', userId)
          .maybeSingle();

        if (existing) {
          // Update existing preferences
          const { error } = await supabase
            .from('user_notification_preferences')
            .update({
              ...preferences,
              updated_at: new Date().toISOString(),
            })
            .eq('smartuser_id', userId);

          if (error) {
            console.error('[NotificationPreferences] Error updating preferences:', error);
            return false;
          }
        } else {
          // Insert new preferences
          const { error } = await supabase
            .from('user_notification_preferences')
            .insert({
              smartuser_id: userId,
              ...preferences,
            });

          if (error) {
            console.error('[NotificationPreferences] Error creating preferences:', error);
            return false;
          }
        }
      } else {
        // Handle anonymous user preferences
        const { data: existing } = await supabase
          .from('user_notification_preferences')
          .select('id')
          .is('smartuser_id', null)
          .maybeSingle();

        if (existing) {
          // Update existing anonymous preferences
          const { error } = await supabase
            .from('user_notification_preferences')
            .update({
              ...preferences,
              updated_at: new Date().toISOString(),
            })
            .is('smartuser_id', null);

          if (error) {
            console.error('[NotificationPreferences] Error updating anonymous preferences:', error);
            return false;
          }
        } else {
          // Insert new anonymous preferences
          const { error } = await supabase
            .from('user_notification_preferences')
            .insert({
              smartuser_id: null,
              ...preferences,
            });

          if (error) {
            console.error('[NotificationPreferences] Error creating anonymous preferences:', error);
            return false;
          }
        }
      }

      return true;
    } catch (error) {
      console.error('[NotificationPreferences] Error updating preferences:', error);
      return false;
    }
  }

  async requestPushPermission(): Promise<boolean> {
    if (Platform.OS === 'web') {
      console.log('[NotificationPreferences] Push notifications not supported on web');
      return false;
    }

    try {
      return await oneSignalService.requestPermission();
    } catch (error) {
      console.error('[NotificationPreferences] Error requesting permission:', error);
      return false;
    }
  }

  async openSystemSettings(): Promise<void> {
    if (Platform.OS === 'web') {
      return;
    }

    try {
      await Linking.openSettings();
    } catch (error) {
      console.error('[NotificationPreferences] Error opening settings:', error);
      throw error;
    }
  }
}

export const notificationPreferencesService = new NotificationPreferencesService();
